import { useContext, useEffect, useRef } from "react";

import MotionContext from "./MotionContext";
import prefersReducedMotion from "./prefers-reduced-motion";

// One passive listener, one frame in flight, one call per painted frame.
//
// The theme's own parallax (LandingPageHeader.js, unrouted) writes
// style.transform from inside a non-passive scroll handler. That is wrong three
// times over: the listener blocks the scroll thread until it returns, a trackpad
// fires scroll several times per painted frame so the same transform is computed
// and thrown away repeatedly, and its effect has no dependency array so it
// resubscribes on every render.
//
// `pauseForReducedMotion` is the difference between an animation and a readout.
// A parallax is decoration and stops when the reader has asked for less motion.
// The scroll progress bar is not decoration — it reports where in the document
// the reader is, the same thing the scrollbar reports — so it keeps updating,
// and only its smoothing is dropped in the stylesheet. Pausing it would leave a
// bar pinned at zero for the whole page, which is worse than no bar at all.
export default function useRafScroll(
  onScroll,
  { pauseForReducedMotion = true } = {}
) {
  const { enabled } = useContext(MotionContext);
  // Held in a ref so a caller that rebuilds its closure every render — which is
  // every caller, since the closure reads props — does not tear the listener
  // down and put an identical one back each time.
  const handler = useRef(onScroll);
  handler.current = onScroll;

  useEffect(() => {
    if (!enabled) return undefined;
    if (pauseForReducedMotion && prefersReducedMotion()) return undefined;
    if (typeof window.requestAnimationFrame !== "function") return undefined;

    let frame = null;

    const run = () => {
      frame = null;
      handler.current(window.scrollY || 0);
    };

    // Coalescing: a frame already queued absorbs every event that arrives before
    // it runs, so a burst of twenty scroll events costs one call, not twenty.
    const schedule = () => {
      if (frame === null) frame = window.requestAnimationFrame(run);
    };

    // Once on mount, before any event. <ScrollRestoration> puts the reader back
    // mid-page inside a layout effect and fires no scroll event doing it, so a
    // hero that only updated on `scroll` would open drawn for scrollY 0 and snap
    // into place on the reader's first wheel click. BackToTop.js records the same
    // lesson for the same reason.
    run();

    window.addEventListener("scroll", schedule, { passive: true });
    window.addEventListener("resize", schedule, { passive: true });
    return () => {
      if (frame !== null) window.cancelAnimationFrame(frame);
      window.removeEventListener("scroll", schedule);
      window.removeEventListener("resize", schedule);
    };
  }, [enabled, pauseForReducedMotion]);
}
